import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Briefcase,
  BarChart3,
  Menu,
  X,
  ChevronLeft,
  Sparkles,
  User,
  LogOut,
  LogIn,
  Settings,
  Users,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import CoinButton from './CoinButton';

const candidateNavigation = [
  { name: 'My Dashboard', href: '/dashboard', icon: BarChart3 },
  { name: 'Job Openings', href: '/jobs', icon: Briefcase },
  { name: 'My Sessions', href: '/interviews', icon: BarChart3 },
  { name: 'My Profile', href: '/profile', icon: User },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const recruiterNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'My Jobs', href: '/jobs', icon: Briefcase },
  { name: 'Candidates', href: '/candidates', icon: Users },
  { name: 'Interviews', href: '/interviews', icon: BarChart3 },
  { name: 'Create Job', href: '/jobs/create', icon: Plus },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  onOpenCustomInterview?: () => void;
}

export default function Sidebar({ collapsed, setCollapsed, onOpenCustomInterview }: SidebarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation(); const pathname = location.pathname;
  const navigate = useNavigate();
  const { signOut, user, isRecruiter, isRecruiterValue, organizationData } = useAuth();

  const navigation = isRecruiterValue ? recruiterNavigation : candidateNavigation;

  useEffect(() => {
    setMounted(true);
    console.log('[Sidebar] Mounted. User:', user ? 'logged in' : 'not logged in');
  }, [user]);

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    if (href === '/interview') {
      return pathname === '/interview';
    }
    if (href === '/jobs/create') {
      return pathname === '/jobs/create';
    }
    if (href === '/jobs') {
      return pathname === '/jobs' || (pathname.startsWith('/jobs/') && pathname !== '/jobs/create');
    }
    return pathname.startsWith(href);
  };

  return (
    <>
      <button
        type="button"
        className={`lg:hidden fixed top-4 z-50 p-2 rounded-xl bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-300 ${
          mobileMenuOpen ? 'right-4' : 'left-4'
        }`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-700" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700" />
        )}
      </button>

      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen transition-all duration-300 ease-in-out
          bg-white border-r border-gray-200
          ${collapsed ? 'w-20' : 'w-64'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          <div className={`p-6 border-b border-gray-200 space-y-4 ${collapsed ? 'px-4' : ''}`}>
            <div className="flex items-center justify-between">
              {!collapsed && (
                <Link to="/" className="flex items-center gap-1">
                  <div className="relative w-[168px] h-[48px]">
                    <img src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/teech-interviews%20logo%20cropped%20black.jpg"
                      alt="Tech Interviews Logo"
                      style={{width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'left'}}
                      className=""

                    />
                  </div>
                </Link>
              )}
              {collapsed && (
                <Link to="/" className="flex items-center justify-center mx-auto">
                  <div className="relative w-8 h-8">
                    <img src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/teech-interviews%20logo%20cropped%20black.jpg"
                      alt="Tech Interviews Logo"
                      style={{width: '100%', height: '100%', objectFit: 'contain'}}
                      className=""

                    />
                  </div>
                </Link>
              )}
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="hidden lg:flex p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className={`w-5 h-5 text-gray-600 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {mounted && user && (
              <CoinButton collapsed={collapsed} />
            )}
          </div>

          {mounted && !collapsed && user && (
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-green-50">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <div className="flex-1 min-w-0">
                  {isRecruiterValue && organizationData ? (
                    <>
                      <p className="text-xs font-medium text-green-900 truncate">
                        {organizationData.name}
                      </p>
                      <p className="text-xs text-green-700 truncate">
                        Recruiter
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-green-900 truncate">
                        Signed in as
                      </p>
                      <p className="text-xs text-green-700 truncate">
                        {user.email}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${active
                      ? 'bg-green-50 text-green-700 font-semibold shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.name : ''}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-green-600' : 'text-gray-500'}`} />
                  {!collapsed && (
                    <span className="text-sm">{item.name}</span>
                  )}
                  {!collapsed && active && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-green-600" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-200 space-y-3">
            {isRecruiterValue ? (
              <>
                <div className={`${collapsed ? 'hidden' : 'block'} p-4 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 text-white`}>
                  <h3 className="font-semibold mb-1 text-sm">Need a new job posting?</h3>
                  <p className="text-xs text-green-100 mb-3">
                    Quick create with interview setup
                  </p>
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenCustomInterview?.();
                    }}
                    className="w-full bg-white text-green-600 hover:bg-green-50 shadow-none"
                    size="sm"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Quick Create Job
                  </Button>
                </div>
                {collapsed && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenCustomInterview?.();
                    }}
                    className="w-full p-3 rounded-xl bg-green-100 text-green-600 hover:bg-green-200 transition-colors flex items-center justify-center"
                    title="Quick Create Job"
                  >
                    <Sparkles className="w-5 h-5" />
                  </button>
                )}
              </>
            ) : (
              <>
                <div className={`${collapsed ? 'hidden' : 'block'} p-4 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 text-white`}>
                  <h3 className="font-semibold mb-1 text-sm">Got an interview coming up?</h3>
                  <p className="text-xs text-green-100 mb-3">
                    Create a custom interview now
                  </p>
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenCustomInterview?.();
                    }}
                    className="w-full bg-white text-green-600 hover:bg-green-50 shadow-none"
                    size="sm"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Start Custom Interview
                  </Button>
                </div>
                {collapsed && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenCustomInterview?.();
                    }}
                    className="w-full p-3 rounded-xl bg-green-100 text-green-600 hover:bg-green-200 transition-colors flex items-center justify-center"
                    title="Start Custom Interview"
                  >
                    <Sparkles className="w-5 h-5" />
                  </button>
                )}
              </>
            )}

            {mounted && (
              <>
                {user ? (
                  <button
                    onClick={async () => {
                      console.log('[Sidebar] Sign out button clicked');
                      console.log('[Sidebar] Current user:', user?.email);
                      console.log('[Sidebar] Current path:', window.location.pathname);
                      console.log('[Sidebar] User object:', user);

                      try {
                        console.log('[Sidebar] Calling signOut()...');
                        await signOut();
                        console.log('[Sidebar] signOut() completed successfully');

                        console.log('[Sidebar] Navigating to /login...');
                        navigate('/login');
                        console.log('[Sidebar] Navigation complete');
                      } catch (error) {
                        console.error('[Sidebar] Error during sign out:', error);
                        console.error('[Sidebar] Error details:', JSON.stringify(error, null, 2));
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 ${collapsed ? 'justify-center' : ''}`}
                    title="Sign Out"
                  >
                    <LogOut className="w-5 h-5" />
                    {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white bg-green-600 hover:bg-green-700 transition-all duration-200 ${collapsed ? 'justify-center' : ''}`}
                    title="Sign In"
                  >
                    <LogIn className="w-5 h-5" />
                    {!collapsed && <span className="text-sm font-medium">Sign In</span>}
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </aside>

    </>
  );
}
